import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { motion } from "framer-motion"
import { useLanguage } from "../context/LanguageContext";
import close from "../assets/images/close.svg"
import arrow from "../assets/images/down-arrow.svg"
import activeArrow from "../assets/images/active-down-arrow.svg"
import { fadeDown, staggerContainer } from "../lib/motion"

type FormVariant = "getInTouch" | "bookDemo";

interface props {
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    variant?: FormVariant;
}
const GetInTouchModal = ({ setIsModalOpen, variant = "getInTouch" }: props) => {
    const { translation, isRTL, lang } = useLanguage();
    const [isCompanySizeOpen, setIsCompanySizeOpen] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', phoneNumber: '', companyName: '', companySize: '', message: '', consent: false });
    const [isMounted, setIsMounted] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

    const sourceLabel = variant === "bookDemo" ? "Book a Demo" : "Get in Touch";

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitStatus("loading");

        const payload = {
            text: `New website lead (${sourceLabel})`,
            blocks: [
                {
                    type: "header",
                    text: {
                        type: "plain_text",
                        text: ` ${sourceLabel} – Website Lead `,
                        emoji: true,
                    },
                },
                {
                    type: "context",
                    elements: [
                        {
                            type: "mrkdwn",
                            text: sourceLabel,
                        },
                    ],
                },
                { type: "divider" },
                {
                    type: "section",
                    fields: [
                        {
                            type: "mrkdwn",
                            text: `*:bust_in_silhouette: Name*\n${formData.name || "—"}`,
                        },
                        {
                            type: "mrkdwn",
                            text: `*:email: Email*\n${formData.email || "—"}`,
                        },
                        {
                            type: "mrkdwn",
                            text: `*:phone: Phone*\n${formData.phoneNumber || "—"}`,
                        },
                        {
                            type: "mrkdwn",
                            text: `*:office: Company*\n${formData.companyName || "—"}`,
                        },
                        {
                            type: "mrkdwn",
                            text: `*:chart_with_upwards_trend: Company Size*\n${formData.companySize || "N/A"}`,
                        },
                        {
                            type: "mrkdwn",
                            text: `*:calendar: Received*\n${new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`,
                        },
                    ],
                },
                ...(formData.message
                    ? [
                        { type: "divider" },
                        {
                            type: "section",
                            text: {
                                type: "mrkdwn",
                                text: `*:speech_balloon: Message*\n${formData.message}`,
                            },
                        },
                    ]
                    : []),
            ],
        };

        try {
            const response = await fetch("/api/slack", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                console.error("Failed to send message to Slack", await response.text());
                setSubmitStatus("error");
                
                const errorToast = document.createElement('div');
                errorToast.className = 'fixed top-8 left-1/2 -translate-x-1/2 z-[200] bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg font-medium animate-in fade-in slide-in-from-top-2 duration-300';
                errorToast.textContent = translation.submitError;
                document.body.appendChild(errorToast);
                
                setTimeout(() => {
                    errorToast.remove();
                    setSubmitStatus("idle");
                }, 4000);
                return;
            }
            setSubmitStatus("success");
            setFormData({ name: '', email: '', phoneNumber: '', companyName: '', companySize: '', message: '', consent: false });
            
            // Show toast notification
            const toast = document.createElement('div');
            toast.className = 'fixed top-8 left-1/2 -translate-x-1/2 z-[200] bg-softGreen text-white px-6 py-3 rounded-lg shadow-lg font-medium animate-in fade-in slide-in-from-top-2 duration-300';
            toast.textContent = translation.submitSuccess;
            document.body.appendChild(toast);
            setIsModalOpen(false);

            setTimeout(() => {
                toast.remove();
            }, 2000);
        } catch (error) {
            console.error("Error sending message to Slack", error);
            setSubmitStatus("error");
            
            // Show error toast notification
            const errorToast = document.createElement('div');
            errorToast.className = 'fixed top-8 left-1/2 -translate-x-1/2 z-[200] bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg font-medium animate-in fade-in slide-in-from-top-2 duration-300';
            errorToast.textContent = translation.submitError;
            document.body.appendChild(errorToast);
            
            setTimeout(() => {
                errorToast.remove();
                setSubmitStatus("idle");
            }, 4000);
        }
    }

    useEffect(() => {
        const close = () => setIsCompanySizeOpen(false)
        document.addEventListener("click", close)
        return () => document.removeEventListener("click", close)
    }, [])

    useEffect(() => {
        setIsMounted(true)
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [])

    if (!isMounted) return null

    return createPortal(
        <motion.div
            className="fixed inset-0 z-[100] bg-primary bg-opacity-70 flex items-center justify-center px-5"
            onClick={() => setIsModalOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
        >
            <motion.form
                onClick={(e) => e.stopPropagation()}
                onSubmit={onSubmit}
                className="relative w-[20.8rem] lg:w-[29.75rem] bg-white rounded-[1.25rem] lg:rounded-[1.875rem] p-8 sm:p-10 lg:p-12 flex flex-col gap-4 sm:gap-[1.375rem] lg:gap-8"
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.98 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
            >
                <button type="button" className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`} onClick={() => setIsModalOpen(false)}>
                    <img src={close} alt="Close" />
                </button>
                <motion.div
                    className="flex flex-col gap-4 sm:gap-[1.375rem] lg:gap-8"
                    variants={staggerContainer(0.08, 0.04)}
                    initial="hidden"
                    animate="show"
                >
                    <motion.div className="flex flex-col gap-[0.375rem] lg:gap-3 text-primary" variants={fadeDown}>
                        <h2 className="text-2xl sm:text-[1.75rem] lg:text-[2rem] leading-[100%] font-bold">
                            {variant === "bookDemo" ? translation.bookDemo : translation.getInTouch}
                        </h2>
                        <p className="text-sm sm:text-base lg:text-lg leading-[1.25]">{translation.contactDetails}</p>
                    </motion.div>
                    <motion.div className="flex flex-col gap-3 sm:gap-[1.125rem] md:gap-6 lg:gap-8 max-h-[50vh] overflow-y-auto custom-scrollbar pe-2 -me-2" variants={fadeDown}>
                        <div className="flex flex-col gap-1 text-primary">
                            {formData.name && <label className="text-sm leading-[1.18] font-bold">{translation.formName}</label>}
                            <input
                                type="text"
                                placeholder={translation.formName}
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e?.target?.value || '' })}
                                required
                                className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg"
                            />
                        </div>
                        <div className="flex flex-col gap-1 text-primary">
                            {formData.email && <label className="text-sm leading-[1.18] font-bold">{translation.formEmail}</label>}
                            <input
                                type="email"
                                placeholder={translation.formEmail}
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e?.target?.value || '' })}
                                required
                                className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg"
                            />
                        </div>
                        <div className="flex flex-col gap-1 text-primary">
                            {formData.phoneNumber && <label className="text-sm leading-[1.18] font-bold">{translation.formPhoneNumber}</label>}
                            <input
                                type="tel"
                                placeholder={translation.formPhoneNumber}
                                value={formData.phoneNumber}
                                onChange={(e) => setFormData({ ...formData, phoneNumber: e?.target?.value || '' })}
                                required
                                pattern="[0-9\+\-\s\(\)]{10,}"
                                title="Please enter a valid phone number"
                                dir={isRTL ? "rtl" : "ltr"}
                                className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg"
                            />
                        </div>
                        <div className="flex flex-col gap-1 text-primary">
                            {formData.companyName && <label className="text-sm leading-[1.18] font-bold">{translation.formCompanyName}</label>}
                            <input
                                type="text"
                                placeholder={translation.formCompanyName}
                                value={formData.companyName}
                                onChange={(e) => setFormData({ ...formData, companyName: e?.target?.value || '' })}
                                required
                                className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg"
                            />
                        </div>
                        <div className="flex flex-col gap-1 relative">
                            {formData.companySize && (<label className="text-sm leading-[1.18] font-bold text-primary">{translation.companySize}</label>)}
                            <span className={`${lang === "ar" ? "" : "hidden"} pointer-events-none absolute left-2 bottom-1 -translate-y-1/2`}>
                                <img src={isCompanySizeOpen ? activeArrow : arrow} alt="arrow" />
                            </span>
                            <button
                                type="button"
                                onClick={() => setIsCompanySizeOpen((prev) => !prev)}
                                className={
                                    `text-start w-full border-b border-[#DCDFE1] py-2 bg-transparent focus:outline-none text-base lg:text-lg
                                ${formData.companySize ? "text-primary" : "text-[#8E9CA7]"}`
                                }
                            >
                                {formData.companySize || translation.companySize}
                            </button>
                            <span className={`${lang === "en" ? "" : "hidden"} pointer-events-none absolute right-2 bottom-1 -translate-y-1/2`}>
                                <img src={isCompanySizeOpen ? activeArrow : arrow} alt="arrow" />
                            </span>
                            {isCompanySizeOpen && (
                                <div className="absolute bottom-full mb-2 z-50 w-full max-h-[40vh] overflow-y-auto rounded-xl bg-white shadow-xl border border-gray-200">
                                    {["1-10", "11-50", "51-100", "100+"].map((size) => (
                                        <button
                                            key={size}
                                            type="button"
                                            onClick={() => {
                                                setFormData({ ...formData, companySize: size })
                                                setIsCompanySizeOpen(false)
                                            }}
                                            className={`flex items-center w-full text-left px-4 py-3 text-sm sm:text-base hover:bg-gray-100 transition ${size === formData.companySize && "bg-gray-100 font-semibold"}`}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-1 text-primary">
                            {formData.message && <label className="text-sm leading-[1.18] font-bold">{translation.formMessage}</label>}
                            <input
                                type="text"
                                placeholder={translation.formMessage}
                                value={formData.message}
                                onChange={(e) => setFormData({ ...formData, message: e?.target?.value || '' })}
                                className="w-full border-b border-[#DCDFE1] py-2 focus:outline-none text-base lg:text-lg"
                            />
                        </div>
                        {/* <div className="flex gap-2 items-start">
                            <input type="checkbox" id="terms" className="mt-0.5 accent-primary" checked={formData.consent} onChange={(e) => setFormData({ ...formData, consent: e?.target?.checked || false })} />
                            <label htmlFor="terms" className="text-xs leading-[1.18]  text-primary">
                                <span className={`block ${lang === "ar" ? "font-arabic" : ""}`}>
                                    {translation.formConsent}
                                </span>
                            </label>
                        </div> */}
                    </motion.div>
<motion.div className="flex flex-col gap-3 pt-2 lg:pt-3" variants={fadeDown}>
                    <motion.button
                        className={`w-full`}
                        type="submit"
                        disabled={submitStatus === "loading" /*|| !formData.consent*/}
                    >
                        <div className={`h-11 lg:h-[3.625rem] rounded-[6.25rem] py-3 lg:py-4 px-[3.75rem] bg-softGreen hover:bg-[#6BB37F] disabled:opacity-70 disabled:cursor-not-allowed text-base sm:text-lg lg:text-[1.375rem] font-semibold leading-[100%] text-white flex items-center justify-center gap-2`}>
                            {submitStatus === "loading" ? translation.submitLoading : translation.send}
                        </div>
                    </motion.button>
                </motion.div>
                </motion.div>
            </motion.form>
        </motion.div>
        , document.body)
}

export default GetInTouchModal
